import { ROLES } from '@helpers';
import { OPERATOR_RESTRICTIONS_ENABLED, OPERATOR_RESTRICTIONS, ITEM_TYPE_TO_LEVEL } from '@config/operatorRestrictions/constants';

// Checks if a specific item is restricted for operators in a given category
export const isRestrictedForOperator = (role: string, category: string, item: string): boolean => {
  if (!OPERATOR_RESTRICTIONS_ENABLED) return false;
  if (role !== ROLES.OPERATOR) return false;
  const restrictions = OPERATOR_RESTRICTIONS[category as keyof typeof OPERATOR_RESTRICTIONS];
  return Array.isArray(restrictions) && restrictions.includes(item);
};

// Filters a list of items based on operator restrictions for a given category
export const getOperatorAllowedItems = (role: string, category: string, items: any[], keyField = 'value'): any[] => {
  if (!OPERATOR_RESTRICTIONS_ENABLED) return items;
  if (role !== ROLES.OPERATOR) return items;
  const restrictions = OPERATOR_RESTRICTIONS[category as keyof typeof OPERATOR_RESTRICTIONS];
  const restricted = Array.isArray(restrictions) ? restrictions : [];
  return items.filter((item) => {
    const value = typeof item === 'string' ? item : item[keyField];
    return !restricted.includes(value);
  });
};

// Helper to get itemType from suggestion lookupKeyFields (sometimes needs to be inferred from ID field)
const getItemTypeFromLookupKeyFields = (lookupKeyFields: any[] | null): string | null => {
  if (!lookupKeyFields) return null;
  // First try direct ItemType field
  const directType = lookupKeyFields.find((f) => f.name === 'ItemType')?.value;
  if (directType) return directType;
  // Infer from ID field names
  const idFieldMapping: Record<string, string> = {
    PartId: 'Part',
    NcFileId: 'NcFile',
    ToolId: 'Tool',
    DocumentId: 'ImageDocument',
  };
  for (const field of lookupKeyFields) {
    if (idFieldMapping[field.name]) {
      return idFieldMapping[field.name];
    }
  }
  return null;
};

// Filters search results (from "All" tab) based on operator restrictions
export const filterSearchResultsForOperator = (role: string, results: any[]): any[] => {
  if (!OPERATOR_RESTRICTIONS_ENABLED) return results;
  if (role !== ROLES.OPERATOR) return results;
  if (!results || !Array.isArray(results)) return results;
  const allowedLevels = OPERATOR_RESTRICTIONS.searchAllowedInAll || [];
  return results.filter((item) => {
    const itemType = item.itemType || getItemTypeFromLookupKeyFields(item.lookupKeyFields);
    if (!itemType) return false;
    const level = ITEM_TYPE_TO_LEVEL[itemType as keyof typeof ITEM_TYPE_TO_LEVEL];
    return level && allowedLevels.includes(level);
  });
};

// Check if operator should be restricted to OperatorPanel only (web/tablet)
export const isOperatorOnlyOP = (role: string): boolean => {
  return OPERATOR_RESTRICTIONS_ENABLED && role === ROLES.OPERATOR && OPERATOR_RESTRICTIONS.operatorOnlyOP;
};

// Check if "Go To Main App" should be hidden
export const shouldHideGoToMainApp = (role: string): boolean => {
  return OPERATOR_RESTRICTIONS_ENABLED && role === ROLES.OPERATOR && OPERATOR_RESTRICTIONS.hideGoToMainApp;
};
